import { useState, useEffect, useCallback } from 'react';
import { useAdminSession } from '../context/AdminSessionContext';
import { useAbortableTask } from './useAbortableTask';

export function useEnquiryReview(record, initialLanguage) {
  const { api } = useAdminSession();
  const task = useAbortableTask();
  const [review, setReview] = useState(null);
  const [messages, setMessages] = useState([]);
  const [language, setLanguage] = useState(initialLanguage);
  const [status, setStatus] = useState('loading');
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const path = `/admin/reviews/${record.kind}/${record.id}`;
  const sessionId = record.session_id;

  useEffect(() => {
    const operation = task.start(); setStatus('loading'); setError('');
    const history = sessionId ? api.get(`/chat/${sessionId}/messages`, operation.signal) : Promise.resolve([]);
    Promise.all([api.get(path, operation.signal), history]).then(([data, conversation]) => {
      if (!task.isCurrent(operation)) return;
      setReview(data); setMessages(conversation); setDirty(false);
      if (data) setLanguage(data.language);
    }).catch(() => { if (task.isCurrent(operation)) setError('load'); })
      .finally(() => { if (task.isCurrent(operation)) setStatus('idle'); });
    return task.cancel;
  }, [api, path, sessionId, task]);

  const generate = useCallback(async () => {
    const operation = task.start(); setStatus('generating'); setError('');
    try {
      await api.stream(path, { language }, { signal: operation.signal, onResult: data => {
        if (!task.isCurrent(operation)) return;
        setReview(data); setDirty(false);
      } });
    } catch (failure) { if (task.isCurrent(operation)) setError(failure.message); }
    finally { if (task.isCurrent(operation)) setStatus('idle'); }
  }, [api, path, language, task]);

  const edit = useCallback((field, value) => {
    setReview(previous => ({ ...previous, [field]: value })); setDirty(true);
  }, []);
  const save = useCallback(async () => {
    const operation = task.start(); setStatus('saving'); setError('');
    try {
      const data = await api.patch(path, { priority: review.priority, priority_reason: review.priority_reason, follow_up: review.follow_up }, operation.signal);
      if (!task.isCurrent(operation)) return false;
      setReview(data); setDirty(false); return true;
    } catch (failure) { if (task.isCurrent(operation)) setError(failure.message); return false; }
    finally { if (task.isCurrent(operation)) setStatus('idle'); }
  }, [api, path, review, task]);

  return { review, messages, language, setLanguage, status, dirty, error, generate, edit, save };
}